import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  abortExtraction,
  type CriticalDateUserRowApi,
  getExtraction,
  retryExtractionCategories,
  startExtraction,
  type TenderExtractionApi,
  updateExtractionCriticalDates,
  updateExtractionSubmissionDeadline,
} from "@/api/tender-api"

import { TENDER_QUERY_KEYS } from "./use-tender-cases"

function isExtractionComplete(data: TenderExtractionApi): boolean {
  return data.status === "completed" || data.status === "partial" || data.status === "failed"
}

export function useTenderExtraction(
  caseId: string | undefined,
  runId: string | undefined,
  options?: { poll?: boolean }
) {
  const poll = options?.poll ?? true
  return useQuery({
    queryKey: TENDER_QUERY_KEYS.extraction(caseId ?? "", runId ?? ""),
    queryFn: () => getExtraction(caseId!, runId!),
    enabled: !!caseId && !!runId,
    retry: 2,
    refetchInterval: (query) => {
      if (!poll) return false
      const data = query.state.data
      if (data && isExtractionComplete(data)) return false
      if (query.state.status === "error") return data ? 10_000 : false
      if (!data) return 5_000
      return 3_000
    },
  })
}

export function useStartExtraction(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ runId, modelId }: { runId: string; modelId?: string }) =>
      startExtraction(caseId!, runId, modelId),
    // Settled, not success: the request may have auto-joined the caller
    // server-side before failing, so case state must refresh either way.
    onSettled: (_data, _error, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.extraction(caseId ?? "", runId),
      })
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.case(caseId ?? ""),
      })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

export function useRetryExtractionCategories(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ runId, categories }: { runId: string; categories: string[] }) =>
      retryExtractionCategories(caseId!, runId, categories),
    onSettled: (_data, _error, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.extraction(caseId ?? "", runId),
      })
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.case(caseId ?? ""),
      })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

export function useAbortExtraction(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ runId }: { runId: string }) => abortExtraction(caseId!, runId),
    onSettled: (_data, _error, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.extraction(caseId ?? "", runId),
      })
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.case(caseId ?? ""),
      })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

export function useUpdateExtractionSubmissionDeadline(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      runId,
      submissionDeadline,
      reason,
    }: {
      runId: string
      submissionDeadline: string | null
      reason?: string
    }) =>
      updateExtractionSubmissionDeadline({
        caseId: caseId!,
        runId,
        submissionDeadline,
        reason,
      }),
    onSuccess: (data, { runId }) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.extraction(caseId ?? "", runId), data)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.case(caseId ?? ""),
      })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}

export function useUpdateExtractionCriticalDates(caseId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      runId,
      removedIds,
      userRows,
    }: {
      runId: string
      removedIds: string[]
      userRows: CriticalDateUserRowApi[]
    }) =>
      updateExtractionCriticalDates({
        caseId: caseId!,
        runId,
        removedIds,
        userRows,
      }),
    onSuccess: (data, { runId }) => {
      queryClient.setQueryData(TENDER_QUERY_KEYS.extraction(caseId ?? "", runId), data)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_QUERY_KEYS.case(caseId ?? ""),
      })
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.cases })
    },
  })
}
